import {
  computed,
  Injectable,
  Injector,
  signal,
  TemplateRef,
  Type,
  ViewContainerRef,
  type WritableSignal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { map, startWith, switchMap } from 'rxjs';

// eslint-disable-next-line @nx/enforce-module-boundaries
import type { CosDrawerComponent } from '@cosmos/ui-drawer';

/**
 * Drawer service that manages state and interactions related to a drawer component.
 * This service can be used to open, close, and toggle the drawer,
 * as well as what content should be displayed within it.
 */
@Injectable({
  providedIn: 'root',
})
export class DrawerService {
  private _connectedDrawer: WritableSignal<{
    drawer: CosDrawerComponent;
    vcr: ViewContainerRef;
  } | null> = signal(null);

  readonly connected = computed(() => !!this._connectedDrawer());

  private _drawer$ = toObservable(this._connectedDrawer).pipe(
    map((connectedDrawer) => connectedDrawer?.drawer)
  );

  readonly isOpen$ = this._drawer$.pipe(
    switchMap((drawer) =>
      drawer ? drawer.openedChange.pipe(startWith(drawer.opened)) : [false]
    ),
    map((opened) => opened || false)
  );

  readonly isOpen = toSignal(this.isOpen$);

  connect(drawer: CosDrawerComponent, vcr: ViewContainerRef) {
    if (this._connectedDrawer()) {
      this.disconnect();
    }
    this._connectedDrawer.set({
      drawer,
      vcr,
    });
  }

  disconnect() {
    const connectedDrawer = this._connectedDrawer();
    if (connectedDrawer) {
      connectedDrawer.vcr.clear();
    }
    this._connectedDrawer.set(null);
  }

  attachComponent(componentType: Type<any>, options?: { injector?: Injector }) {
    const connectedDrawer = this._connectedDrawer();
    if (!connectedDrawer) {
      if (ngDevMode) {
        throw new Error(
          'No drawer is connected. Connect a drawer before trying to open it.'
        );
      }
      return;
    }
    const { vcr } = connectedDrawer;
    // First clear any existing content in the drawer
    vcr.clear();
    // Then create the new component inside the drawer
    const componentRef = vcr.createComponent(componentType, options);
    componentRef.changeDetectorRef.detectChanges();
    return componentRef;
  }

  attachTemplate(templateRef: TemplateRef<any>) {
    const connectedDrawer = this._connectedDrawer();
    if (!connectedDrawer) {
      if (ngDevMode) {
        throw new Error(
          'No drawer is connected. Connect a drawer before trying to open it.'
        );
      }
      return;
    }
    const { vcr } = connectedDrawer;
    // First clear any existing content in the drawer
    vcr.clear();
    // Then create the new template content inside the drawer
    const embeddedViewRef = vcr.createEmbeddedView(templateRef);
    embeddedViewRef.detectChanges();
    return embeddedViewRef;
  }

  openDrawer() {
    const connectedDrawer = this._connectedDrawer();
    if (!connectedDrawer) {
      if (ngDevMode) {
        throw new Error(
          'No drawer is connected. Connect a drawer before trying to open it.'
        );
      }
      return;
    }
    const { drawer } = connectedDrawer;
    return drawer.open();
  }

  closeDrawer() {
    const connectedDrawer = this._connectedDrawer();
    if (!connectedDrawer) {
      if (ngDevMode) {
        throw new Error(
          'No drawer is connected. Connect a drawer before trying to close it.'
        );
      }
      return;
    }
    const { drawer } = connectedDrawer;
    return drawer.close();
  }
}
